import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useSelectedProgram, UNASSIGNED_PROGRAM } from '../../contexts/SelectedProgramContext';
import db, { collection, query, where, getDocs } from '../../firebase/firestore';
import { callFunction } from '../../firebase/functions';
import { useGroups } from '../../hooks/useGroups';
import { UserPlusIcon, ClipboardDocumentIcon, CheckIcon, XMarkIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline';
import { Sheet, Segmented, Btn, IconBtn } from './ui';

const INVITE_FUNCTIONS = { player: 'inviteAcademyPortalPlayer', coach: 'inviteCoach', parent: 'inviteParent' };

const fieldLabelStyle = { display: 'block', fontSize: 11.5, fontWeight: 600, color: 'var(--t2)', marginBottom: 5 };
const fieldInputStyle = {
  width: '100%', height: 42, padding: '0 12px', border: '1px solid var(--border)',
  borderRadius: 'var(--r-input)', fontSize: 13.5, background: 'var(--surface)',
};

// Lets a coach or academy admin bring someone straight into the Academy
// Portal without them ever going through CRM/Onboarding — for players who
// signed up outside the system (an academy adopting the portal mid-season
// with an existing roster), another coach, or an additional parent for a
// player who's already here (the player's original guardian invite handles
// the first parent automatically — this is for a second one). Groups
// picked here are applied the moment the invite is accepted, so they land
// in the right group/program from their very first login rather than
// needing a second step.
//
// `variant="icon"` renders as an icon-only trigger (mobile header, desktop
// topbar); `variant="button"` renders the desktop sidebar's full-width
// labelled primary button. Both open the same Sheet.
export default function InviteDropdown({ variant = 'icon' }) {
  const { t } = useTranslation();
  const { academyId } = useAuth();
  const { groups } = useGroups();
  const { selectedProgramId } = useSelectedProgram();
  const [open, setOpen] = useState(false);
  const [role, setRole] = useState('player'); // 'player' | 'coach' | 'parent'
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [groupIds, setGroupIds] = useState([]);
  const [working, setWorking] = useState(null); // 'link' | 'email' | null
  const [error, setError] = useState('');
  const [result, setResult] = useState(null); // { link, emailed }
  const [copied, setCopied] = useState(false);

  const [players, setPlayers] = useState(null); // null = not loaded yet
  const [playerSearch, setPlayerSearch] = useState('');
  const [selectedPlayer, setSelectedPlayer] = useState(null);

  // Only offered alongside a 'player' invite — inviting a parent for a
  // player already on the portal has its own entry point (role: 'parent'
  // above, or handleAddParents in GroupDetail.jsx once the player's in a
  // group), so this checkbox exists purely to avoid staff having to
  // remember a second trip through this same sheet for a brand-new family.
  const [alsoInviteParent, setAlsoInviteParent] = useState(false);
  const [parentDisplayName, setParentDisplayName] = useState('');
  const [parentEmail, setParentEmail] = useState('');
  const [parentInviteError, setParentInviteError] = useState('');

  useEffect(() => {
    if (role !== 'parent' || players !== null || !academyId) return;
    getDocs(query(collection(db, 'players'), where('academyId', '==', academyId)))
      .then((snap) => setPlayers(snap.docs.map((d) => ({ id: d.id, ...d.data() }))))
      .catch(() => setPlayers([]));
  }, [role, players, academyId]);

  const toggleGroup = (id) => setGroupIds((g) => (g.includes(id) ? g.filter((x) => x !== id) : [...g, id]));

  const reset = () => {
    setDisplayName('');
    setEmail('');
    setGroupIds([]);
    setError('');
    setResult(null);
    setCopied(false);
    setPlayerSearch('');
    setSelectedPlayer(null);
    setAlsoInviteParent(false);
    setParentDisplayName('');
    setParentEmail('');
    setParentInviteError('');
  };

  const close = () => {
    setOpen(false);
    reset();
  };

  const invite = async (sendEmail) => {
    if (role === 'parent' && !selectedPlayer) {
      setError(t('academy:inviteDropdown.errorSelectPlayer', 'Pick which player this parent belongs to first.'));
      return;
    }
    if (!displayName.trim() || !email.trim()) {
      setError(t('academy:inviteDropdown.errorEnterNameEmail', 'Enter a name and email address first.'));
      return;
    }
    if (role === 'player' && alsoInviteParent && (!parentDisplayName.trim() || !parentEmail.trim())) {
      setError(t('academy:inviteDropdown.errorEnterParentNameEmail', "Enter the parent's name and email, or untick \"also invite a parent\"."));
      return;
    }
    setWorking(sendEmail ? 'email' : 'link');
    setError('');
    setParentInviteError('');
    try {
      const fn = callFunction(INVITE_FUNCTIONS[role]);
      const payload = { displayName: displayName.trim(), email: email.trim(), sendEmail, groupIds: role === 'parent' ? [] : groupIds };
      if (role === 'parent') payload.playerId = selectedPlayer.id;
      // A player invited straight into the Academy Portal (bypassing
      // Onboarding, where a program would normally get assigned) needs a
      // programId of their own too — otherwise they silently drop out of
      // every academy-wide event/post scoped to whichever program is
      // currently active (see useAcademyEvents.js/useFeed.js's own
      // fallback for players who already slipped through without one).
      if (role === 'player' && selectedProgramId && selectedProgramId !== UNASSIGNED_PROGRAM) {
        payload.programId = selectedProgramId;
      }
      const res = await fn(payload);

      let parentInvited = false;
      if (role === 'player' && alsoInviteParent) {
        try {
          await callFunction('inviteParent')({
            playerId: res.data.playerId,
            displayName: parentDisplayName.trim(),
            email: parentEmail.trim(),
            sendEmail,
            groupIds,
          });
          parentInvited = true;
        } catch (parentErr) {
          setParentInviteError(parentErr.message || t('academy:inviteDropdown.parentInviteFailed', "The player was invited, but the parent invite failed — use the Parent tab above to try again."));
        }
      }

      setResult({ link: res.data.inviteLink, emailed: sendEmail, parentInvited });
      if (!sendEmail) {
        try {
          await navigator.clipboard.writeText(res.data.inviteLink);
          setCopied(true);
        } catch { /* clipboard may be unavailable — link is still shown to copy manually */ }
      }
    } catch (err) {
      setError(err.message || t('academy:inviteDropdown.createInviteFailed', 'Failed to create invite.'));
    } finally {
      setWorking(null);
    }
  };

  const copyAgain = async () => {
    if (!result?.link) return;
    try {
      await navigator.clipboard.writeText(result.link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* ignore */ }
  };

  const roleOptions = [
    { v: 'player', l: t('academy:inviteDropdown.roles.player', 'Player') },
    { v: 'coach', l: t('academy:inviteDropdown.roles.coach', 'Coach') },
    { v: 'parent', l: t('academy:inviteDropdown.roles.parent', 'Parent') },
  ];

  return (
    <>
      {variant === 'button' ? (
        <Btn icon={UserPlusIcon} full onClick={() => setOpen(true)} style={{ marginBottom: 12 }}>
          {t('academy:inviteDropdown.inviteToPortal', 'Invite to portal')}
        </Btn>
      ) : (
        <IconBtn icon={UserPlusIcon} tone="dark" s={32} label={t('academy:inviteDropdown.inviteAriaLabel', 'Invite')} onClick={() => setOpen(true)} />
      )}

      <Sheet
        open={open}
        onClose={close}
        title={t('academy:inviteDropdown.panelTitle', 'Invite to the Academy Portal')}
        sub={t('academy:inviteDropdown.panelSubtitle', 'For anyone joining outside the usual sign-up flow — already-signed players, another coach, or an extra parent.')}
        footer={!result && (
          <div style={{ display: 'flex', gap: 8, paddingBottom: 4 }}>
            <Btn variant="ghost" icon={ClipboardDocumentIcon} disabled={!!working} onClick={() => invite(false)} style={{ flex: 1 }}>
              {working === 'link' ? t('academy:inviteDropdown.creatingLink', 'Creating…') : t('academy:inviteDropdown.copyLink', 'Copy link')}
            </Btn>
            <Btn icon={PaperAirplaneIcon} disabled={!!working} onClick={() => invite(true)} style={{ flex: 1.3 }}>
              {working === 'email' ? t('academy:inviteDropdown.sendingInvite', 'Sending…') : t('academy:inviteDropdown.sendInvite', 'Send invite')}
            </Btn>
          </div>
        )}
      >
        {!result ? (
          <div style={{ paddingBottom: 16 }}>
            <Segmented full value={role} options={roleOptions} onChange={(v) => { setRole(v); setResult(null); }} />
            <div style={{ height: 16 }} />

            {role === 'parent' && (
              selectedPlayer ? (
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
                  border: '1px solid var(--border)', borderRadius: 'var(--r-input)', background: 'var(--sunken)',
                  padding: '9px 12px', marginBottom: 12,
                }}>
                  <span style={{ fontSize: 12.5, color: 'var(--t2)' }}>{t('academy:inviteDropdown.parentOf', 'Parent of {{name}}', { name: selectedPlayer.fullName })}</span>
                  <button type="button" onClick={() => setSelectedPlayer(null)} style={{ color: 'var(--t3)' }}>
                    <XMarkIcon style={{ width: 14, height: 14 }} />
                  </button>
                </div>
              ) : (
                <label style={{ display: 'block', marginBottom: 12 }}>
                  <span style={fieldLabelStyle}>{t('academy:inviteDropdown.roles.player', 'Player')}</span>
                  <input
                    type="text"
                    placeholder={t('academy:inviteDropdown.playerSearchPlaceholder', 'Search for the player…')}
                    value={playerSearch}
                    onChange={(e) => setPlayerSearch(e.target.value)}
                    style={fieldInputStyle}
                  />
                  {playerSearch.trim() && (
                    <div style={{ marginTop: 6, maxHeight: 128, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 10 }}>
                      {(players || [])
                        .filter((p) => p.fullName?.toLowerCase().includes(playerSearch.trim().toLowerCase()))
                        .slice(0, 20)
                        .map((p, i) => (
                          <button
                            key={p.id}
                            type="button"
                            onClick={() => { setSelectedPlayer(p); setPlayerSearch(''); }}
                            className="ap-tap"
                            style={{
                              display: 'block', width: '100%', textAlign: 'left', padding: '9px 12px', fontSize: 12.5,
                              color: 'var(--t1)', borderTop: i ? '1px solid var(--border)' : 'none', background: 'var(--surface)',
                            }}
                          >
                            {p.fullName}
                          </button>
                        ))}
                      {players && !players.some((p) => p.fullName?.toLowerCase().includes(playerSearch.trim().toLowerCase())) && (
                        <p style={{ padding: '9px 12px', fontSize: 12.5, color: 'var(--t3)' }}>{t('academy:inviteDropdown.noPlayersFound', 'No players found.')}</p>
                      )}
                    </div>
                  )}
                </label>
              )
            )}

            <label style={{ display: 'block', marginBottom: 12 }}>
              <span style={fieldLabelStyle}>
                {role === 'player' ? t('academy:inviteDropdown.namePlaceholderPlayer', "Player's full name")
                  : role === 'coach' ? t('academy:inviteDropdown.namePlaceholderCoach', "Coach's full name")
                    : t('academy:inviteDropdown.namePlaceholderParent', "Parent's full name")}
              </span>
              <input type="text" value={displayName} onChange={(e) => setDisplayName(e.target.value)} style={fieldInputStyle} />
            </label>
            <label style={{ display: 'block', marginBottom: 12 }}>
              <span style={fieldLabelStyle}>{t('academy:inviteDropdown.emailPlaceholder', 'Email address')}</span>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} style={fieldInputStyle} />
            </label>

            {role === 'player' && (
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 9, cursor: 'pointer' }}>
                  <span style={{
                    width: 18, height: 18, borderRadius: 5, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    border: `1.5px solid ${alsoInviteParent ? 'var(--accent)' : 'var(--border-2)'}`,
                    background: alsoInviteParent ? 'var(--accent)' : 'transparent',
                  }}>
                    <input type="checkbox" checked={alsoInviteParent} onChange={(e) => setAlsoInviteParent(e.target.checked)} style={{ display: 'none' }} />
                    {alsoInviteParent && <CheckIcon style={{ width: 12, height: 12, strokeWidth: 3, color: '#fff' }} />}
                  </span>
                  <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--t1)' }}>{t('academy:inviteDropdown.alsoInviteParent', 'Also invite a parent for this player')}</span>
                </label>
                {alsoInviteParent && (
                  <div style={{ marginTop: 10, paddingLeft: 27 }}>
                    <label style={{ display: 'block', marginBottom: 10 }}>
                      <span style={fieldLabelStyle}>{t('academy:inviteDropdown.namePlaceholderParent', "Parent's full name")}</span>
                      <input type="text" value={parentDisplayName} onChange={(e) => setParentDisplayName(e.target.value)} style={fieldInputStyle} />
                    </label>
                    <label style={{ display: 'block' }}>
                      <span style={fieldLabelStyle}>{t('academy:inviteDropdown.emailPlaceholder', 'Email address')}</span>
                      <input type="email" value={parentEmail} onChange={(e) => setParentEmail(e.target.value)} style={fieldInputStyle} />
                    </label>
                  </div>
                )}
              </div>
            )}

            {/* Not offered for a parent invite — inviteParent already
                mirrors every group the player is currently in server-side
                (see functions/index.js), so picking those here would just
                be a no-op duplicate; a parents-only group has no players in
                it to mirror from, so a coach adds a parent to one of those
                as a separate step once the parent's on the portal, not
                here (see the panel subtitle / GroupDetail.jsx). */}
            {groups.length > 0 && role !== 'parent' && (
              <div style={{ marginBottom: 12 }}>
                <div className="ap-ovl" style={{ marginBottom: 8 }}>{t('academy:inviteDropdown.addToGroups', 'Add to group(s) — optional')}</div>
                <div style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
                  {groups.map((g, i) => {
                    const checked = groupIds.includes(g.id);
                    return (
                      <button
                        key={g.id}
                        type="button"
                        onClick={() => toggleGroup(g.id)}
                        className="ap-tap"
                        style={{
                          display: 'flex', alignItems: 'center', gap: 11, width: '100%', padding: '11px 12px', textAlign: 'left',
                          borderTop: i ? '1px solid var(--border)' : 'none', background: 'var(--surface)',
                        }}
                      >
                        <span style={{
                          width: 18, height: 18, borderRadius: 5, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                          border: `1.5px solid ${checked ? 'var(--accent)' : 'var(--border-2)'}`,
                          background: checked ? 'var(--accent)' : 'transparent',
                        }}>{checked && <CheckIcon style={{ width: 12, height: 12, strokeWidth: 3, color: '#fff' }} />}</span>
                        <span style={{ fontSize: 13.5, fontWeight: 500, flex: 1, color: 'var(--t1)' }}>{g.name}</span>
                      </button>
                    );
                  })}
                </div>
                <p style={{ fontSize: 11.5, color: 'var(--t3)', marginTop: 8, lineHeight: 1.5 }}>
                  {t('academy:inviteDropdown.willBeAddedNote', "They'll already be in these once they set their password.")}
                </p>
              </div>
            )}

            {error && <p style={{ fontSize: 12, color: '#b91c1c' }}>{error}</p>}
          </div>
        ) : (
          <div style={{ paddingBottom: 16 }}>
            <p style={{
              fontSize: 12.5, color: 'var(--accent-ink)', background: 'var(--accent-light)',
              border: '1px solid var(--border)', borderRadius: 10, padding: '10px 12px', lineHeight: 1.5,
            }}>
              {result.emailed
                ? t('academy:inviteDropdown.inviteEmailSent', 'Invite email sent to {{email}}.', { email })
                : t('academy:inviteDropdown.inviteLinkCreated', 'Invite link created — copy it below to send yourself (WhatsApp, text, etc).')}
              {groupIds.length > 0 && ` ${t('academy:inviteDropdown.addedToGroupsNote', "They'll be added to {{count}} group once they set their password.", { count: groupIds.length })}`}
              {result.parentInvited && ` ${t('academy:inviteDropdown.parentAlsoInvited', 'A separate invite was also sent to the parent.')}`}
            </p>
            {parentInviteError && <p style={{ fontSize: 12, color: '#b91c1c', marginTop: 8 }}>{parentInviteError}</p>}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12 }}>
              <input type="text" readOnly value={result.link} onFocus={(e) => e.target.select()} style={{ ...fieldInputStyle, color: 'var(--t2)', fontSize: 12.5 }} />
              <button type="button" onClick={copyAgain} title={t('academy:inviteDropdown.copyLink', 'Copy link')} className="ap-tap" style={{
                flexShrink: 0, width: 42, height: 42, borderRadius: 'var(--r-input)', border: '1px solid var(--border)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--t2)',
              }}>
                {copied ? <CheckIcon style={{ width: 16, height: 16, color: 'var(--accent-ink)' }} /> : <ClipboardDocumentIcon style={{ width: 16, height: 16 }} />}
              </button>
            </div>
            <button type="button" onClick={reset} style={{ marginTop: 12, fontSize: 12.5, fontWeight: 600, color: 'var(--accent-ink)' }}>
              {t('academy:inviteDropdown.inviteSomeoneElse', 'Invite someone else')}
            </button>
          </div>
        )}
      </Sheet>
    </>
  );
}
